import { ORDER_LINE_TYPE, } from "../../../../assets/react/Webshop/types/ShoppingCart";
// Carried across pages because the arms arrive with the picture-set payload,
// which the order can beat to the receipt.
const EXPERIMENT_ARMS_STORAGE_KEY = "epms.vwo.experimentArms";
/**
 * Reports webshop conversions to VWO (Wingify) as Data360 events. Consent is
 * VWO's own (handlesOwnConsent), not EpmsAnalytics' - see
 * docs/plans/drafts/consent-unification.md.
 */
export class VwoProvider {
    constructor() {
        this.handlesOwnConsent = true;
        this.experimentArms = {};
        this.currency = "NOK";
        this.experimentArms = readStoredExperimentArms();
        // Re-assert on every page: a push made while consent was still pending was
        // dropped by VWO. Last-value-wins per visitor, so repeating is harmless.
        this.pushExperimentArms();
    }
    setCurrency(code) {
        this.currency = code;
    }
    /**
     * The arms EPMS assigned this subject, as VWO visitor attributes. Each must
     * exist in the dashboard first, with Text as its data type.
     */
    setExperimentArms(arms) {
        this.experimentArms = arms;
        storeExperimentArms(arms);
        this.pushExperimentArms();
    }
    /**
     * A client-side route change, reported to VWO as a page load. The SmartCode
     * evaluates page-match rules once per document, which in the mobile webshop
     * means once per session, so a campaign whose changed elements the router
     * has unmounted never re-applies them on its own. "activate" re-runs that
     * qualification against the URL given.
     *
     * The visitor keeps the arm they were bucketed into, but VWO does count a
     * page view, so heatmaps, recordings and goal URLs follow the SPA. Note
     * that the SmartCode's anti-flicker only guards the real document load: on
     * a virtual one the un-varied page paints before the changes come back.
     */
    onVirtualPageView(url) {
        this.pushToVwo(["activate", { virtualPageUrl: url }]);
    }
    onPurchaseSuccessful(order) {
        var _a, _b;
        const total = order.summary.totalPrice;
        const shipping = this.lineTotal(order, ORDER_LINE_TYPE.DELIVERY);
        this.trackEvent("purchase", {
            order_id: order.uuid,
            revenue: round(total),
            revenue_ex_shipping: round(total - shipping),
            shipping: round(shipping),
            // Summed, not first-match: an order gets one VAT line per distinct rate.
            tax: round(this.lineTotal(order, ORDER_LINE_TYPE.VAT)),
            currency: this.currency,
            item_count: order.orderLines.filter((line) => line.type === ORDER_LINE_TYPE.PRODUCT).length,
            coupon: (_b = (_a = order.rebateCode) === null || _a === void 0 ? void 0 : _a.code) !== null && _b !== void 0 ? _b : "",
            has_digital_products: order.containDigitalProducts,
        });
    }
    onAddToCart(items) {
        this.trackItemsEvent("add_to_cart", items);
    }
    onRemoveFromCart(items) {
        this.trackItemsEvent("remove_from_cart", items);
    }
    onViewCart(items) {
        this.trackItemsEvent("view_cart", items);
    }
    onBeginCheckout(items) {
        this.trackItemsEvent("begin_checkout", items);
    }
    onCartChange(cartUpdate) {
        if (cartUpdate.newOrderLines && cartUpdate.newOrderLines.length > 0) {
            this.trackItemsEvent("add_to_cart", cartUpdate.newOrderLines);
        }
        if (cartUpdate.removedOrderLines &&
            cartUpdate.removedOrderLines.length > 0) {
            this.trackItemsEvent("remove_from_cart", cartUpdate.removedOrderLines);
        }
    }
    setAllowTracking(_value) {
        // Intentionally empty: consent reaches VWO through the consent bridge in
        // VwoAnalyticsProvider, which detects the visitor's actual consent tool.
    }
    trackItemsEvent(eventName, items) {
        this.trackEvent(eventName, {
            value: round(items.reduce((sum, orderLine) => sum + orderLine.actualLinePrice, 0)),
            currency: this.currency,
            item_count: items.length,
        });
    }
    trackEvent(eventName, properties) {
        const payload = Object.assign(Object.assign({}, properties), this.experimentProperties());
        console.log(`[VWO-event] ${eventName}`, payload);
        this.pushToVwo(["event", eventName, payload]);
    }
    lineTotal(order, type) {
        return order.orderLines
            .filter((line) => line.type === type)
            .reduce((sum, line) => sum + line.linePrice, 0);
    }
    pushExperimentArms() {
        const attributes = this.experimentProperties();
        if (Object.keys(attributes).length === 0) {
            return;
        }
        this.pushToVwo(["visitor", attributes]);
    }
    experimentProperties() {
        const properties = {};
        for (const [clientKey, arm] of Object.entries(this.experimentArms)) {
            properties[experimentPropertyName(clientKey)] = arm;
        }
        return properties;
    }
    pushToVwo(payload) {
        var _a;
        (_a = getVwoQueue()) === null || _a === void 0 ? void 0 : _a.push(payload);
    }
}
/** The queue VwoAnalyticsProvider sets up, taken over by the tag on load. */
function getVwoQueue() {
    const queue = window.Wingify;
    return queue && typeof queue.push === "function" ? queue : null;
}
/** addToCartUseBuyNow -> experiment_add_to_cart_use_buy_now */
function experimentPropertyName(clientKey) {
    return ("experiment_" +
        clientKey.replace(/[A-Z]/g, (letter) => "_" + letter.toLowerCase()));
}
function readStoredExperimentArms() {
    try {
        const stored = window.sessionStorage.getItem(EXPERIMENT_ARMS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    }
    catch (_a) {
        return {};
    }
}
function storeExperimentArms(arms) {
    try {
        window.sessionStorage.setItem(EXPERIMENT_ARMS_STORAGE_KEY, JSON.stringify(arms));
    }
    catch (_a) {
        // Storage unavailable (private mode); arms then only apply to this page.
    }
}
function round(value) {
    return Math.round(value * 100) / 100;
}
